import { Locale, setDefaultOptions } from "date-fns";
import { ar, es, th } from "date-fns/locale";
import i18n from "i18next";
import Backend from "i18next-http-backend";
import moment from "moment-timezone";
import "moment/locale/ar";
import "moment/locale/es";
import "moment/locale/th";
import {
  DEFAULT_LANGUAGE,
  getLanguageFromPath,
  SUPPORTED_LANGUAGES,
} from "plos/src/utils/language";
import { initReactI18next } from "react-i18next";
import { namespaces, resources } from "./resources";

const SUPPORTED_LANGS = new Set<string>(SUPPORTED_LANGUAGES);
const DEFAULT_LANG = DEFAULT_LANGUAGE;
const isProd = import.meta.env.PROD;

const resolveI18nLanguage = () => {
  /* Allow i18next's "cimode" only in non-prod environments
   * "cimode" will return the translation keys instead of strings, useful for debugging */
  if (!isProd && localStorage.getItem("lang") === "cimode") {
    return "cimode";
  }

  return getLanguageFromPath(window.location.pathname);
};

const lng = resolveI18nLanguage();

document.documentElement.lang = SUPPORTED_LANGS.has(lng) ? lng : DEFAULT_LANG;

const dateLocales: Record<string, Locale> = {
  ar,
  es,
  th,
};

// Update moment, date-fns, and document lang
i18n.on("languageChanged", function (lng) {
  // date-fns will fallback to english as default language when passed undefined (e.g lang = "cimode", will default to english)
  setDefaultOptions({ locale: dateLocales[lng] ?? undefined });
  // moment does not fallback to a default language, so we explicitly check and pass english default
  moment.locale(SUPPORTED_LANGS.has(lng) ? lng : DEFAULT_LANG);
  document.documentElement.lang = SUPPORTED_LANGS.has(lng) ? lng : DEFAULT_LANG;
});

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    ns: namespaces,
    defaultNS: "common",
    resources,
    // Required when using resources with a backend - tells i18next that only
    // bundled languages (en) are complete, so it fetches others via the backend
    partialBundledLanguages: true,
    lng,
    saveMissing: !isProd, // report missing translation keys during development
    appendNamespaceToCIMode: true, // show the namespace alongside the key when lng is "cimode"
    fallbackLng: "en", // to stop trying to fetch dev translations
    debug: !isProd,
    interpolation: {
      escapeValue: false, // react already safe from xss as escaped by default
    },
  });
